import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Camera, 
  Calendar, 
  Home, 
  AlertTriangle, 
  Loader2,
  ImageOff,
  MessageSquare,
  CheckCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PublicGallery = () => {
  const { shareToken } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/gallery/${shareToken}`);
        if (!res.ok) {
          if (res.status === 404) {
            setError('Cette galerie n\'existe pas ou le lien a expiré.');
          } else {
            setError('Erreur lors du chargement de la galerie.');
          }
          return;
        }
        const data = await res.json();
        setGallery(data);
      } catch (err) {
        setError('Impossible de charger la galerie.');
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, [shareToken]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long',
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return '';
    if (photo.startsWith('http://') || photo.startsWith('https://')) {
      return photo;
    }
    // Use public photo endpoint with share token
    return `${API_URL}/api/public/photo?path=${encodeURIComponent(photo)}&share_token=${shareToken}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#1F3A8A] mx-auto" />
          <p className="mt-4 text-slate-600">Chargement de la galerie...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <ImageOff className="h-16 w-16 text-slate-300 mx-auto mb-4" />
            <h1 className="text-xl font-bold text-slate-800 mb-2">Galerie introuvable</h1>
            <p className="text-slate-500">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalPhotos = (gallery?.photos?.length || 0) + 
                      (gallery?.problem_photos?.length || 0) + 
                      (gallery?.control_photos?.length || 0);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-[#1F3A8A] text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-blue-200 text-sm">
              <Camera className="h-4 w-4" />
              <span>Galerie photos partagée</span>
            </div>
            <img 
              src="/logo-mcb-full.png" 
              alt="Ma Conciergerie Bretonne" 
              className="h-14 w-auto object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold flex items-center gap-3">
            <Home className="h-6 w-6" />
            {gallery?.property_name}
          </h1>
          <div className="flex items-center gap-4 mt-3 text-blue-100">
            <div className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>{formatDate(gallery?.task_date)}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Stats */}
        <div className="flex gap-3">
          <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
            <Camera className="h-3 w-3 mr-1" />
            {totalPhotos} photo{totalPhotos > 1 ? 's' : ''}
          </Badge>
          {gallery?.problem_photos?.length > 0 && (
            <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
              <AlertTriangle className="h-3 w-3 mr-1" />
              {gallery.problem_photos.length} signalement{gallery.problem_photos.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        {/* Photos du ménage */}
        {gallery?.photos?.length > 0 && (
          <Card>
            <CardContent className="p-4">
              <h2 className="font-bold text-slate-800 mb-4 flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Photos du ménage ({gallery.photos.length})
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {gallery.photos.map((photo, idx) => (
                  <div 
                    key={`photo-${idx}`}
                    className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border border-slate-200"
                    onClick={() => setSelectedPhoto(getPhotoUrl(photo))}
                  >
                    <img 
                      src={getPhotoUrl(photo)} 
                      alt={`Photo ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photos de problèmes */}
        {gallery?.problem_photos?.length > 0 && (
          <Card className="border-amber-200">
            <CardContent className="p-4">
              <h2 className="font-bold text-amber-700 mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Photos de problèmes signalés ({gallery.problem_photos.length})
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {gallery.problem_photos.map((photo, idx) => (
                  <div 
                    key={`problem-${idx}`}
                    className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border-2 border-amber-300"
                    onClick={() => setSelectedPhoto(getPhotoUrl(photo))}
                  >
                    <img 
                      src={getPhotoUrl(photo)} 
                      alt={`Problème ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Photos de contrôle qualité */}
        {gallery?.control_photos?.length > 0 && (
          <Card className="border-blue-200">
            <CardContent className="p-4">
              <h2 className="font-bold text-blue-700 mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                Photos de contrôle qualité ({gallery.control_photos.length})
              </h2>
              {gallery.control_comment && (
                <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm text-blue-800">
                  {gallery.control_comment}
                </div>
              )}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {gallery.control_photos.map((photo, idx) => (
                  <div 
                    key={`control-${idx}`}
                    className="aspect-square rounded-lg overflow-hidden cursor-pointer hover:opacity-90 transition-opacity border-2 border-blue-300"
                    onClick={() => setSelectedPhoto(getPhotoUrl(photo))}
                  >
                    <img 
                      src={getPhotoUrl(photo)} 
                      alt={`Contrôle ${idx + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Aucune photo */}
        {totalPhotos === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <ImageOff className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Aucune photo dans cette galerie</p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <div className="text-center text-xs text-slate-400 py-4">
          Galerie générée par PlayMCB • MA CONCIERGERIE BRETONNE
        </div>
      </div>

      {/* Lightbox */}
      {selectedPhoto && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <img 
            src={selectedPhoto} 
            alt="Photo agrandie"
            className="max-w-full max-h-full object-contain"
          />
          <button 
            className="absolute top-4 right-4 text-white text-4xl font-light hover:opacity-70"
            onClick={() => setSelectedPhoto(null)}
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};

export default PublicGallery;
